/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 * 
 * This prevents the entire app from crashing due to a single component error.
 * 
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

interface ErrorBoundaryProps {
    /** Child components to wrap */
    children: ReactNode;
    /** Optional custom fallback component */
    fallback?: ReactNode;
}

interface ErrorBoundaryState {
    /** Whether an error has been caught */
    hasError: boolean;
    /** The error that was caught */
    error: Error | null;
}

// =============================================================================
// ERROR FALLBACK COMPONENT
// =============================================================================

interface ErrorFallbackProps {
    error: Error | null;
    onRetry: () => void;
}

/**
 * Default error fallback UI
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onRetry }) => {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full text-center">
                {/* Error Icon */}
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertTriangle className="w-10 h-10 text-[#c00035]" />
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Something went wrong
                </h1>

                {/* Description */}
                <p className="text-gray-600 mb-6">
                    We're sorry, but something unexpected happened.
                    Please try refreshing the page.
                </p>

                {/* Error details (development only) */}
                {process.env.NODE_ENV === 'development' && error && (
                    <div className="mb-6 p-4 bg-gray-100 rounded-lg text-left">
                        <p className="text-sm font-mono text-[#c00035] break-all">
                            {error.message}
                        </p>
                    </div>
                )}

                {/* Retry Button */}
                <button
                    onClick={onRetry}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-haat-red text-white font-medium rounded-lg hover:bg-haat-red/90 transition-colors focus:outline-none focus:ring-2 focus:ring-haat-red focus:ring-offset-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                </button>

                {/* Home Link */}
                <div className="mt-4">
                    <a
                        href="/"
                        className="text-sm text-gray-500 hover:text-haat-red transition-colors"
                    >
                        Go back to home
                    </a>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// ERROR BOUNDARY CLASS COMPONENT
// =============================================================================

/**
 * Error Boundary Component
 * 
 * Must be a class component because React's error boundaries
 * require lifecycle methods that are not available in functional components.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
        };
    }

    /**
     * Update state when an error is caught
     */
    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {
            hasError: true,
            error,
        };
    }

    /**
     * Log error information
     * In production, you would send this to an error monitoring service
     */
    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Only log in development - in production, send to monitoring service
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error);
            console.error('Error info:', errorInfo.componentStack);
        }

        // TODO: In production, send to error monitoring service like Sentry
        // Example: Sentry.captureException(error, { extra: errorInfo });
    }

    /**
     * Reset error state and retry
     */
    handleRetry = (): void => {
        this.setState({
            hasError: false,
            error: null,
        });
    };

    render(): ReactNode {
        const { hasError, error } = this.state;
        const { children, fallback } = this.props;

        if (hasError) {
            // Use custom fallback if provided, otherwise use default
            if (fallback) {
                return fallback;
            }
            return <ErrorFallback error={error} onRetry={this.handleRetry} />;
        }

        return children;
    }
}

export default ErrorBoundary;

